import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  editAutoAccept,
  fetchInvitationRequests,
  updateInvitationStatus,
} from "../../redux/eventManagementSlice";
import User from "../User";
import UserAvatar from "../UserAvatar";
import Event from "./Event";

const getStatusChipColor = (status) => {
  switch (status) {
    case "accepted":
      return "success";
    case "declined":
      return "error";
    case "pending":
      return "warning";
    default:
      return "default";
  }
};

const getCommitmentChipColor = (commitment) => {
  switch (commitment) {
    case "going":
      return "success";
    case "interested":
    case "no-response":
      return "info";
    case "invitation-requested":
      return "warning";
    default:
      return "default";
  }
};

export default function ManageEvent() {
  const { profile } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { managingEvent, invitationRequests, totalRequests, requestsStatus } =
    useSelector((state) => state.eventManagement);

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (managingEvent?._id) {
      dispatch(
        fetchInvitationRequests({
          eventId: managingEvent._id,
          page: page,
          limit: rowsPerPage,
          sortField: orderBy,
          sortOrder: order,
        })
      );
    }
  }, [dispatch, managingEvent?._id, page, rowsPerPage, orderBy, order]);

  const headCells = useMemo(
    () => [
      {
        id: "username",
        numeric: false,
        disablePadding: false,
        label: t("username"),
      },
      {
        id: "commitment",
        numeric: false,
        disablePadding: false,
        label: t("commitment"),
      },
      {
        id: "createdAt",
        numeric: true,
        disablePadding: false,
        label: t("requestAt"),
      },
      {
        id: "status",
        numeric: true,
        disablePadding: false,
        label: t("status"),
      },
    ],
    [t]
  );

  const handleRequestSort = (property) => {
    if (property === "actions") return;
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAccept = (requestId) => {
    dispatch(updateInvitationStatus({ requestId, status: "accepted" }));
  };
  const handleDecline = (requestId) => {
    dispatch(updateInvitationStatus({ requestId, status: "declined" }));
  };

  return (
    <Box className="w-full mx-auto">
      <Paper
        className="w-full mx-auto"
        elevation={3}
        sx={{
          padding: "2rem 1rem",
          my: 2,
          borderRadius: 3,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          className="text-center"
        >
          {managingEvent?._id ? t("manageEvent") : t("eventNotFound")}
        </Typography>
        {managingEvent?._id && (
          <Event
            event={managingEvent}
            index={0}
            profile={profile}
            interactionDisabled={true}
          />
        )}
        <FormControlLabel
          sx={{ my: 0.5, borderRadius: 1 }}
          control={
            <Checkbox
              checked={managingEvent?.autoAcceptInvitationRequests || false}
              onChange={(e) =>
                dispatch(
                  editAutoAccept({
                    autoAccept: e.target.checked,
                    eventId: managingEvent._id,
                  })
                )
              }
            />
          }
          label={t("autoAcceptInvitationRequests")}
        />
        <Typography variant="h5" component="h2" gutterBottom>
          {t("invitationRequests")}
        </Typography>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? "right" : "left"}
                    padding={headCell.disablePadding ? "none" : "normal"}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    {headCell.sortable === false ? (
                      headCell.label
                    ) : (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : "asc"}
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc"
                              ? t("sortedDescending")
                              : t("sortedAscending")}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {requestsStatus === "loading" ? (
                <TableRow>
                  <TableCell colSpan={headCells.length} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                invitationRequests.map((row) => (
                  <TableRow hover key={row._id}>
                    <TableCell component="th" scope="row">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <UserAvatar userId={row.userID} user={row.user} />
                        {!row.user?.username || row.user?.deletedAccount ? (
                          <Typography>{t("deletedUser")}</Typography>
                        ) : (
                          <User
                            username={row.user?.username}
                            userId={row.userID}
                            showMessage={true}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(row.commitment)}
                        color={getCommitmentChipColor(row.commitment)}
                        size="small"
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {new Date(row.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {row.status === "pending" ? (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleAccept(row._id)}
                          >
                            {t("accept")}
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() => handleDecline(row._id)}
                          >
                            {t("decline")}
                          </Button>
                        </Box>
                      ) : (
                        <Chip
                          label={t(row.status)}
                          color={getStatusChipColor(row.status)}
                          size="small"
                          sx={{ textTransform: "capitalize" }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalRequests}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t("tableRowsPerPage")}
          labelDisplayedRows={({ from, to, count }) => {
            // The 'count' can be -1 if the total is unknown.
            // MUI provides a special key for this case.
            const key =
              count !== -1 ? "tableDisplayedRows" : "tableDisplayedRowsOfMore";
            return t(key, { from, to, count });
          }}
        />
      </Paper>
    </Box>
  );
}
